import _ from "lodash";
import { ENTITY_COLUMNS } from "src/entityList/types";
import { EntityChangeModel } from "src/entityView/types";
import { UpdateMetadataConfigPayload } from "src/metadata/types";
import { AuditLogs, HistoryAuditLogs } from "../auditLog/types";
import { Comment } from "../commentView/types";
import {
  BriefSpread,
  BriefSpreadResponse,
  BriefSpreadStatus,
  SpreadFilter,
  SpreadSort,
  SpreadStatusGroup
} from "../documentList/types";
import { Entity, EntityId, UploadSuccessParams } from "../documentUpload/types";
import {
  ExpireSuccessParams,
  LoginSuccessParams,
  RegisterSuccessParams,
  ResetPasswordSuccessParams
} from "../login/types";
import { RawMetadataConfig } from "../metadata/types";
import { PairOfTokens } from "../redirectComponent/types";
import { RequestWrapper } from "../requestWrapper";
import { SpreadStateChangeModel } from "../spread/model/spread/actions";
import {
  AccountId,
  RuleChangeLogItem,
  Spread,
  SpreadAndComments
} from "../spread/model/spread/types";
import { getReturnCodeMessage } from "./returnCodes";
import { GetBriefSpreadResponse } from "./types";

import { Preferences } from "src/preferences/types";
import { TagAccount, TagArea } from "../spread/chartOfAccounts/Tag/types";

import { ERRORS } from "../constants";
import { ExportReportParams } from "../spread/model/spread/actions";

export interface SpreadUpdateFinancialValueModel {
  financialValueModelId: string;
  userAdjustedValue: number | null;
  nodeId: AccountId;
  period: string;
}

export interface SpreadSavePayload {
  spreadId: string;
  updatedFinancialValueModel: SpreadUpdateFinancialValueModel;
  reason: string;
  version?: number;
  userId?: string;
}

export interface UpdateDocumentExceptionStatePayload {
  spreadId: string;
  documentExceptionId: string;
  state: string;
  version?: number;
  userId?: string;
}

export interface API {
  briefSpread: {
    get: (
      isBankAnalyst: boolean,
      spreadSort: SpreadSort,
      entityId: EntityId,
      spreadFilter: SpreadFilter,
      pageSize: number,
      page?: number,
      query?: string,
      spreadStatus?: SpreadStatusGroup
    ) => Promise<BriefSpreadResponse>;
    deleteSpread: (
      spreadId: string,
      isCreditLens: boolean,
      spreadSort: SpreadSort
    ) => Promise<BriefSpread[]>;
    spreadStateChange: (
      spreadModel: SpreadStateChangeModel,
      isCreditLens: boolean
    ) => Promise<{ spreadStatus?: BriefSpreadStatus; version: number }>;
  };
  spread: {
    get: (
      spreadId: string,
      isCreditLens: boolean,
      bankingAnalyst: boolean,
      environmentName?: string,
      status?: string
    ) => Promise<{ spread: Spread; comments: Comment[]; version: number }>;
    save: (
      spreadSavePayload: SpreadSavePayload,
      isCreditLens: boolean
    ) => Promise<{ spread: Spread; version: number }>;
    unlock: (
      spreadId: string,
      environmentName?: string,
      status?: string,
      hasElasticSearchDelay?: boolean
    ) => Promise<void>;
    exportReport: (spreadId: string, sheetPerPeriod: boolean) => Promise<void>;
    reject: (
      spreadId: string,
      rejectReason: string,
      periods: string[],
      isCreditLens: boolean,
      rejectComments?: string
    ) => Promise<void>;
  };
  login: {
    post: (username: string, password: string) => Promise<LoginSuccessParams>;
    dbsJwtPost: (jwt: string) => Promise<LoginSuccessParams>;
    exchangeCode: (
      code: string,
      grantType: string,
      redirectUri: string,
      scope: string,
      ssoCredentials: string
    ) => Promise<PairOfTokens>;
  };
  expireSession: {
    post: (
      accessToken: string,
      refreshToken: string,
      authType: string
    ) => Promise<ExpireSuccessParams>;
    okta: (url: string) => Promise<any>;
  };
  entities: {
    search: (query: string) => Promise<Entity[]>;
    get: (
      pageSize?: number,
      filter?: string,
      sortColumn?: ENTITY_COLUMNS,
      sortAscending?: boolean,
      getEntityListPage?: any,
      page?: number
    ) => Promise<Entity[]>;
    create: (entity: Entity) => Promise<Entity>;
    edit: (entity: Entity) => Promise<Entity>;
    getOne: (id: string) => Promise<Entity>;
    getAudits: (id: string) => Promise<EntityChangeModel[]>;
  };
  registerUser: {
    post: (
      username: string,
      password: string,
      confirmationCode: string
    ) => Promise<RegisterSuccessParams>;
  };
  resetUserPassword: {
    post: (
      username: string,
      password: string,
      code: string
    ) => Promise<ResetPasswordSuccessParams>;
  };
  uploadFinancialDocument: {
    post: (formData: FormData) => Promise<UploadSuccessParams>;
  };
  updateDocumentExceptionState: {
    post: (
      payload: UpdateDocumentExceptionStatePayload,
      isCreditLens: boolean
    ) => Promise<{ spread: Spread; version: number }>;
  };
  historyAuditLogs: {
    get: (spreadId: string, isCreditLens: boolean) => Promise<HistoryAuditLogs>;
  };
  auditLogs: {
    get: (
      spreadId: string,
      isCreditLens: boolean,
      from?: number,
      size?: number,
      order?: "asc" | "desc",
      orderBy?: string,
      scope?: string
    ) => Promise<AuditLogs>;
  };
  rules: {
    mapRule: (
      spreadId: string,
      rule: RuleChangeLogItem,
      isCreditLens: boolean
    ) => Promise<{ spread: Spread; version: number }>;
    mapUnmappedAccount: (
      spreadId: string,
      unmappedAccountId: string,
      nodeId: string,
      isCreditLens: boolean
    ) => Promise<{ spread: Spread; version: number }>;
    unmapAccount: (
      spreadId: string,
      nodeId: string,
      isCreditLens: boolean
    ) => Promise<{ spread: Spread; version: number }>;
    deleteRules: (
      rules: RuleChangeLogItem[],
      spreadId?: string
    ) => Promise<void>;
    undoRule: (
      spreadId: string,
      currentRule: RuleChangeLogItem,
      previousRule: RuleChangeLogItem,
      isCreditLens: boolean
    ) => Promise<{ spread: Spread; version: number }>;
    getRules: (entityId: string) => Promise<RuleChangeLogItem[]>;
  };
  comments: {
    getComments: (
      spreadId: string,
      isCreditLens: boolean
    ) => Promise<Comment[]>;
    addComment: (
      commentText: string,
      spreadId: string,
      isCreditLens: boolean,
      accountId?: string,
      accountName?: string,
      period?: string
    ) => Promise<{ comment: Comment; version: number }>;
    addUserToReadByForComments: (
      comments: Comment[],
      isCreditLens: boolean,
      spreadId: string
    ) => Promise<boolean>;
    addUserToReadByForBriefspread: (
      spreadId: string
    ) => Promise<{ version: number }>;
  };
  image: {
    get: (
      imageUrl: string,
      isCreditLens: boolean,
      spreadId: string,
      isDbsJwtTokenEnabled: boolean
    ) => Promise<string>;
  };
  metadata: {
    updateConfigForSpread: (
      metadata: UpdateMetadataConfigPayload,
      isCreditLens: boolean
    ) => Promise<{ version: number }>;
    getWorkspaceConfig: (
      isCreditLens: boolean,
      spreadId: string
    ) => Promise<RawMetadataConfig>;
    getClientConfig: (spreadId: string, isCreditLens?: boolean) => Promise<any>;
  };
  tag: {
    save: (
      tagAccount: TagAccount,
      spreadId: string,
      isCreditLens: boolean
    ) => Promise<{ spread: Spread; version: number }>;
    saveArea: (
      area: TagArea,
      isCreditLens: boolean,
      spreadId: string
    ) => Promise<TagArea>;
  };
  preferences: {
    getSupportedLanguages: () => Promise<string[]>;
    getUserPreferences: () => Promise<Preferences>;
    saveUserPreferences: (preferences: any) => Promise<Preferences>;
  };
}

let baseApiUrl = "";
let localAuthPath = "";

let ssoBaseApiUrl = "";

const getBaseApiUrl = () => baseApiUrl;
const getBaseUrl = (isCreditLens: boolean, bypassApigee?: boolean) => {
  if (isCreditLens) {
    return "/api/creditlens";
  } else if (bypassApigee) {
    return "/auth";
  } else {
    return getBaseApiUrl();
  }
};

export const getBaseApiAuthUrl = () => localAuthPath || "/auth";

export const setBaseApiUrl = (url: string) => {
  baseApiUrl = url;
};

export const setLocalAuthPath = (url: string) => {
  localAuthPath = url;
};

export const getSSOBaseApiUrl = () => ssoBaseApiUrl;

export const setSSOBaseApiUrl = (url: string) => {
  ssoBaseApiUrl = url;
};

// Effectively sleeps the response because the ES briefSpread index
// will not refresh for up to 1 second. Sorry, everyone.

const ELASTIC_DELAY_MS = 1000;

export const waitForElasticSearchUpdateDelay = () => {
  return new Promise<void>(resolve => {
    setTimeout(() => {
      resolve();
    }, ELASTIC_DELAY_MS);
  });
};

export class APIError extends Error {
  constructor(message: string) {
    super(message);
  }
}

export const urlEncodeParams = (params: object): string => {
  const apiUrl = new URLSearchParams();
  Object.keys(params).forEach(key => {
    if (Array.isArray(params[key])) {
      params[key].forEach((value: string) => {
        apiUrl.append(key, value);
      });
    } else {
      apiUrl.append(key, params[key]);
    }
  });
  return apiUrl.toString();
};

const retrieveSpread = (
  spreadId: string,
  isCreditLens: boolean,
  bankingAnalyst: boolean,
  environmentName?: string,
  spreadState?: string
) => {
  const apiUrl = `${getBaseUrl(isCreditLens)}/getSpread`;
  if (isCreditLens) {
    return RequestWrapper.to(apiUrl)
      .withCreditLensSpreadIdInHeader(spreadId)
      .request();
  } else {
    return RequestWrapper.to(apiUrl)
      .withAuth()
      .setAuthType()
      .asPost({
        spreadId,
        bankingAnalyst,
        environmentName,
        spreadState
      })
      .asJSON()
      .request();
  }
};

const api: API = {
  briefSpread: {
    get: async (
      isBankAnalyst: boolean,
      spreadSort: SpreadSort,
      entityId: EntityId,
      spreadFilter: SpreadFilter,
      pageSize: number,
      page?: number,
      query?: string,
      statusGroup?: SpreadStatusGroup
    ) => {
      const url = `${getBaseApiUrl()}/getBriefSpreadsUI`;
      const { sortColumn, sortAscending } = spreadSort;

      const params: any = {
        bankingAnalyst: isBankAnalyst,
        sortColumn,
        sortAscending,
        entityId
      };

      if (spreadFilter.active !== undefined && !entityId) {
        params.active = spreadFilter.active;
      }

      if (entityId) {
        params.statusGroup = "FOR_ENTITY_UI_FILTER";
      }

      if (statusGroup && statusGroup.statusGroup === "REJECTED") {
        params.statusGroup = "REJECTED";
      }

      if (page !== undefined && !entityId) {
        params.size = pageSize;
        params.from = page * pageSize;
      }

      if (query) {
        params.searchWord = query;
      }

      const response = await RequestWrapper.to(url)
        .withAuth()
        .setAuthType()
        .withParams(params)
        .request<GetBriefSpreadResponse>();

      const { data } = response;

      if (data.body) {
        return (data.body as unknown) as BriefSpreadResponse;
      } else {
        return {} as BriefSpreadResponse;
      }
    },
    deleteSpread: async (
      spreadId: string,
      isCreditLens: boolean,
      spreadSort: SpreadSort
    ) => {
      const body = { spreadId };
      const { sortColumn, sortAscending } = spreadSort;

      const url = `${getBaseUrl(isCreditLens)}/deleteBriefSpreadById`;
      const response = await RequestWrapper.to(url)
        .withAuth()
        .setAuthType()
        .asPost(body)
        .withParams({
          sortColumn,
          sortAscending
        })
        .asJSON()
        .request();

      const { data } = response;

      await waitForElasticSearchUpdateDelay();

      if (data.body) {
        return data.body.briefSpreadModels as BriefSpread[];
      } else {
        throw new APIError("Brief Spread is empty");
      }
    },
    spreadStateChange: async (
      spreadModel: SpreadStateChangeModel,
      isCreditLens: boolean
    ) => {
      const params = { ...spreadModel };
      const url = `${getBaseUrl(isCreditLens)}/spreadStateChange`;
      const response = await RequestWrapper.to(url)
        .withAuth()
        .setAuthType()
        .asPost(params)
        .asJSON()
        .request();
      const { data } = response;
      const { body, version, returnCode } = data;

      if (returnCode >= 400) {
        const errorMessage = data.message;
        throw new APIError(errorMessage);
      }

      const isV2Api = body === undefined;

      if (!isV2Api) {
        return {
          spreadStatus: body.status as BriefSpreadStatus,
          version
        };
      } else {
        return {
          version
        };
      }
    }
  },
  spread: {
    get: async (
      spreadId: string,
      isCreditLens: boolean,
      bankingAnalyst: boolean,
      environmentName?: string,
      spreadState?: string
    ) => {
      const response = await retrieveSpread(
        spreadId,
        isCreditLens,
        bankingAnalyst,
        environmentName,
        spreadState
      );

      const { data, status } = response;
      const { body, version, returnCode } = data;

      if (!body) {
        throw new APIError("Spread is empty");
      } else {
        if (
          body.spreadUIModel &&
          body.spreadUIModel.financialTemplate.cashFlows === undefined
        ) {
          body.spreadUIModel.financialTemplate.cashFlows = [];
        } else if (
          body.financialTemplate &&
          body.financialTemplate.cashFlows === undefined
        ) {
          body.financialTemplate.cashFlows = [];
        }

        if (
          body.spreadUIModel &&
          body.spreadUIModel.financialTemplate.cashFlowsV2 === undefined
        ) {
          body.spreadUIModel.financialTemplate.cashFlowsV2 = [];
        } else if (
          body.financialTemplate &&
          body.financialTemplate.cashFlowsV2 === undefined
        ) {
          body.financialTemplate.cashFlowsV2 = [];
        }
      }

      if (status === 200 && body.status && body.status === "DELETED") {
        throw new APIError(ERRORS.SPREAD_DELETED_MESSAGE);
      }

      if (
        status === 200 &&
        body.spreadUIModel &&
        body.spreadUIModel.status === "REJECTED"
      ) {
        throw new APIError(ERRORS.SPREAD_REJECTED_MESSAGE);
      }

      if (status === 403 || returnCode === 403) {
        throw new APIError("Spread is not accessible");
      }

      if (isCreditLens || !body.spreadUIModel) {
        // if creditLens OR not a v2 spread
        // TODO: implement the /getSpreadData change to return spread + comments, and update this line
        const result = {
          spread: body as Spread,
          comments: body.comments as Comment[],
          version
        };

        if (response.headers && response.headers["allow-edit"] === "false") {
          window.allowEdit = false;
        }

        return result;
      }

      const spreadAndComments = body as SpreadAndComments;
      const spread =
        spreadAndComments && (spreadAndComments.spreadUIModel as Spread);
      const comments =
        spreadAndComments && (spreadAndComments.comments as Comment[]);

      if (spread) {
        return {
          spread,
          comments,
          version
        };
      } else {
        throw new APIError("Spread is empty");
      }
    },
    save: async (
      spreadSavePayload: SpreadSavePayload,
      isCreditLens: boolean
    ) => {
      const apiUrl = `${getBaseUrl(isCreditLens)}/saveSpread`;
      const { spreadId } = spreadSavePayload;

      if (isCreditLens) {
        spreadSavePayload.userId = "creditlens";
      }
      const response = await new RequestWrapper(apiUrl)
        .withAuth()
        .setAuthType()
        .asPost(spreadSavePayload)
        .asJSON()
        .withParams({ spreadId })
        .request();
      const { data } = response;
      const { body, version, returnCode } = data;

      if (returnCode >= 400) {
        throw new APIError(getReturnCodeMessage(returnCode));
      }

      if (body) {
        if (
          body.financialTemplate &&
          body.financialTemplate.cashFlows === undefined
        ) {
          body.financialTemplate.cashFlows = [];
        }

        if (
          body.financialTemplate &&
          body.financialTemplate.cashFlowsV2 === undefined
        ) {
          body.financialTemplate.cashFlowsV2 = [];
        }

        return {
          spread: body as Spread,
          version
        };
      } else {
        throw new APIError("Spread is empty");
      }
    },
    unlock: async (
      spreadId: string,
      environmentName?: string,
      spreadState?: string,
      hasElasticSearchDelay?: boolean
    ) => {
      await new RequestWrapper(`${getBaseApiUrl()}/unlockSpread`)
        .withAuth()
        .setAuthType()
        .asPost({
          spreadId,
          environmentName,
          spreadState
        })
        .asJSON()
        .request();

      if (hasElasticSearchDelay) {
        await waitForElasticSearchUpdateDelay();
      }
    },
    exportReport: async (spreadId: string, sheetPerPeriod: boolean) => {
      const apiUrl = `${getBaseApiUrl()}/report`;
      try {
        const requestExportSheet: ExportReportParams = { spreadId };
        if (sheetPerPeriod) {
          requestExportSheet.sheetPerPeriod = sheetPerPeriod;
        }

        const response = await new RequestWrapper(apiUrl)
          .withParams(requestExportSheet)
          .withAuth()
          .setAuthType()
          .withOptions({
            responseType: "blob"
          })
          .request();

        const downloadLink = window.document.createElement("a");
        downloadLink.href = window.URL.createObjectURL(
          new Blob([response.data])
        );
        downloadLink.target = "_blank";

        const fileName = response.headers["content-disposition"].split(
          "filename="
        )[1];
        downloadLink.download = fileName;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      } catch (e) {
        throw new APIError("Couldn't export report");
      }
    },
    reject: async (
      spreadId: string,
      rejectReason: string,
      periods: string[],
      isCreditLens: boolean,
      rejectComment?: string
    ) => {
      const apiUrl = `${getBaseUrl(isCreditLens)}/rejectSpread`;
      try {
        const response = await new RequestWrapper(apiUrl)
          .asPost({
            spreadId,
            rejectReason,
            rejectComment,
            periods
          })
          .withAuth()
          .setAuthType()
          .request();

        const { data } = response;

        const { returnCode } = data;

        if (returnCode !== 100) {
          throw new APIError("Error rejecting spread");
        }
      } catch (e) {
        throw new APIError("Couldn't reject spread");
      }
    }
  },
  login: {
    dbsJwtPost: async (jwt: string) => {
      try {
        const response = await new RequestWrapper(
          `${getBaseApiAuthUrl()}/DBSLogin`
        )
          .asPost({ jwt })
          .asJSON()
          .request();

        const { data } = response;

        if ("error" in data) {
          throw new APIError(data.error);
        } else {
          return data as LoginSuccessParams;
        }
      } catch (error) {
        throw new APIError(error.message);
      }
    },
    post: async (username: string, password: string) => {
      try {
        const response = await new RequestWrapper(
          `${getBaseApiAuthUrl()}/login`
        )
          .asPost({ username, password })
          .asJSON()
          .request();

        const { data } = response;

        if ("error" in data) {
          throw new APIError(data.error);
        } else {
          return data as LoginSuccessParams;
        }
      } catch (error) {
        throw new APIError(error.message);
      }
    },
    exchangeCode: async (
      code: string,
      grantType: string,
      redirectUri: string,
      scope: string,
      ssoCredentials: string
    ) => {
      try {
        const response = await new RequestWrapper(
          `${ssoBaseApiUrl}/sso-api/auth/token`
        )
          .withSSOHeaderContent(ssoCredentials)
          .asPost(
            `code=${code}&grant_type=${grantType}&redirect_uri=${redirectUri}&scope=${scope}`
          )
          .request();
        // JSON.parse(response) as Entity[];
        const { id_token, access_token } = response.data;
        return { id_token, access_token } as PairOfTokens;
      } catch (error) {
        throw new APIError(error.message);
      }
    }
  },
  expireSession: {
    post: async (
      accessToken: string,
      refreshToken: string,
      authType: string
    ) => {
      const response = await new RequestWrapper(`${getBaseApiAuthUrl()}/expire`)
        .asPost({ accessToken, refreshToken, authType })
        .asJSON()
        .request();

      const { data } = response;

      return data as ExpireSuccessParams;
    },
    okta: async (url: string) => {
      const response = await new RequestWrapper(
        `${getSSOBaseApiUrl()}/sso-api/v1/token?redirectUrl=${url}`
      )
        .asDelete()
        .request();

      const { data } = response;

      return data;
    }
  },
  registerUser: {
    post: async (username, password, confirmationCode) => {
      const response = await new RequestWrapper(
        `${getBaseApiAuthUrl()}/register`
      )
        .asPost({ username, password, confirmationCode })
        .asJSON()
        .request();

      const { data } = response;

      const parsedBody = JSON.parse(data.body);

      if ("error" in parsedBody) {
        throw new APIError(parsedBody.error);
      } else {
        return JSON.parse(data.body) as RegisterSuccessParams;
      }
    }
  },
  resetUserPassword: {
    post: async (username, password, code) => {
      try {
        const response = await new RequestWrapper(
          `${getBaseApiAuthUrl()}/resetUserPassword`
        )
          .asPost({ username, password, code })
          .asJSON()
          .request();

        const { data, status } = response;

        if (status !== 200) {
          throw new APIError(data);
        } else {
          return { passwordChanged: true } as ResetPasswordSuccessParams;
        }
      } catch (error) {
        throw new APIError(error.message);
      }
    }
  },
  entities: {
    search: async (query: string) => {
      const apiUrl = `${getBaseApiUrl()}/searchEntities`;

      try {
        const response = await new RequestWrapper(apiUrl)
          .withParams({ query })
          .withAuth()
          .setAuthType()
          .asJSON()
          .request();

        const parsedResults = JSON.parse(response.data.body) as Entity[];
        return parsedResults;
      } catch (error) {
        throw new APIError("Couldn't search for entities");
      }
    },
    create: async (entity: Entity) => {
      const apiUrl = `${getBaseApiUrl()}/createEntity`;

      const response = await RequestWrapper.to(apiUrl)
        .withAuth()
        .setAuthType()
        .asPost(entity)
        .asJSON()
        .request();

      await waitForElasticSearchUpdateDelay();

      const { data } = response;

      if (data.returnCode === 414) {
        throw new APIError(data.message);
      }
      if (data.returnCode >= 400 || !data.body) {
        throw new APIError("Error creating entity");
      }

      const parsedResults = JSON.parse(data.body) as Entity;

      return parsedResults;
    },
    edit: async (entity: Entity) => {
      const apiUrl = `${getBaseApiUrl()}/editEntity`;

      try {
        const response = await RequestWrapper.to(apiUrl)
          .withAuth()
          .setAuthType()
          .asPost(entity)
          .asJSON()
          .request();

        if (typeof response.data.body === "string") {
          const data = JSON.parse(response.data.body);
          const parsedResults = data as Entity;

          return parsedResults;
        } else {
          const { returnCode, message } = response.data;

          if (returnCode >= 400) {
            throw new APIError(message);
          }
        }

        throw new APIError("Couldn't edit entity");
      } catch (error) {
        throw new APIError(error.message);
      }
    },
    get: async (
      page?: number,
      filter?: string,
      sortColumn?: string,
      sortAscending?: boolean
    ) => {
      const apiUrl = `${getBaseApiUrl()}/getEntities`;
      sortColumn = sortColumn && sortColumn.toUpperCase();

      let size;
      let from;

      if (page !== undefined) {
        size = 25;
        from = page && 25 * page;
      }

      try {
        const response = await new RequestWrapper(apiUrl)
          .withAuth()
          .setAuthType()
          .asJSON()
          .withParams({ size, from, filter, sortColumn, sortAscending })
          .request();

        const parsedResults = response.data.body as Entity[];
        return parsedResults;
      } catch (error) {
        throw new APIError("Couldn't get entities");
      }
    },
    getOne: async (id: string) => {
      const apiUrl = `${getBaseApiUrl()}/getEntity`;

      try {
        const response = await new RequestWrapper(apiUrl)
          .withAuth()
          .setAuthType()
          .asJSON()
          .withParams({ id })
          .request();

        const parsedResults = JSON.parse(response.data.body) as Entity;
        return parsedResults;
      } catch (error) {
        throw new APIError("Couldn't get entity");
      }
    },
    getAudits: async (id: string) => {
      const apiUrl = `${getBaseApiUrl()}/getEntityAudits`;

      try {
        const response = await new RequestWrapper(apiUrl)
          .withAuth()
          .setAuthType()
          .asJSON()
          .withParams({ id })
          .request();

        const parsedResults = JSON.parse(
          response.data.body
        ) as EntityChangeModel[];
        return parsedResults;
      } catch (error) {
        throw new APIError("Couldn't get entity audit log");
      }
    }
  },
  uploadFinancialDocument: {
    post: async (formData: FormData) => {
      const url = `${getBaseApiUrl()}/spread`;
      const response = await RequestWrapper.to(url)
        .withAuth()
        .setAuthType()
        .asPost(formData)
        .request();

      const { data } = response;

      await waitForElasticSearchUpdateDelay();

      if (data && data.body) {
        const json = {
          message: data.message,
          spreadId: data.body
        };
        return json as UploadSuccessParams;
      } else {
        if (typeof response === "string") {
          throw new APIError(response);
        } else {
          throw new APIError(data.error);
        }
      }
    }
  },
  updateDocumentExceptionState: {
    post: async (
      payload: UpdateDocumentExceptionStatePayload,
      isCreditLens: boolean
    ) => {
      const apiUrl = `${getBaseUrl(isCreditLens)}/updateDocumentExceptionState`;

      const response = await RequestWrapper.to(apiUrl)
        .withAuth()
        .setAuthType()
        .asPost(payload)
        .asJSON()
        .request();

      const { data } = response;
      const { body, version, returnCode } = data;

      if (returnCode >= 400) {
        throw new APIError(getReturnCodeMessage(returnCode));
      }

      if (body) {
        return {
          spread: body as Spread,
          version
        };
      } else {
        throw new APIError("Document Exception was not updated properly.");
      }
    }
  },
  historyAuditLogs: {
    get: async (spreadId: string, isCreditLens: boolean) => {
      const apiUrl = `${getBaseUrl(isCreditLens)}/audit`;

      const response = await RequestWrapper.to(apiUrl)
        .withAuth()
        .setAuthType()
        .withParams({
          spreadID: spreadId
        })
        .request();

      const { data } = response;

      if (data.body) {
        return data.body;
      } else {
        throw new APIError("Audit log is empty");
      }
    }
  },
  auditLogs: {
    get: async (
      spreadId: string,
      isCreditLens: boolean,
      from?: number,
      size?: number,
      order?: "asc" | "desc",
      orderBy?: string,
      scope?: string
    ) => {
      const apiUrl = `${getBaseUrl(isCreditLens)}/v2/spread/audit`;

      const response = await RequestWrapper.to(apiUrl)
        .withAuth()
        .setAuthType()
        .withParams({
          spreadID: spreadId,
          sortOrder: order,
          sortColumn: orderBy,
          from,
          size,
          scope
        })
        .request();

      const { data } = response;

      if (data.body) {
        return data.body;
      } else {
        throw new APIError("Audit log is empty");
      }
    }
  },
  rules: {
    mapRule: async (
      spreadId: string,
      rule: RuleChangeLogItem,
      isCreditLens: boolean
    ) => {
      const apiUrl = `${getBaseUrl(isCreditLens)}/mapRule`;

      const response = await new RequestWrapper(apiUrl)
        .withAuth()
        .setAuthType()
        .asPost({})
        .asJSON()
        .withParams({ ...rule, spreadId })
        .request();

      const { data } = response;
      const { body, version, returnCode } = data;

      if (returnCode >= 400) {
        throw new APIError(getReturnCodeMessage(returnCode));
      }

      if (body) {
        if (
          body.financialTemplate &&
          body.financialTemplate.cashFlows === undefined
        ) {
          body.financialTemplate.cashFlows = [];
        }

        if (
          body.financialTemplate &&
          body.financialTemplate.cashFlowsV2 === undefined
        ) {
          body.financialTemplate.cashFlowsV2 = [];
        }

        return {
          spread: body as Spread,
          version
        };
      } else {
        throw new APIError("Could not create rule");
      }
    },
    mapUnmappedAccount: async (
      spreadId: string,
      unmappedAccountId: string,
      parentNodeId: string,
      isCreditLens: boolean
    ) => {
      const apiUrl = `${getBaseUrl(isCreditLens)}/mapAccount`;

      const response = await new RequestWrapper(apiUrl)
        .withAuth()
        .setAuthType()
        .asPost({})
        .asJSON()
        .withParams({
          spreadId,
          unmappedAccountId,
          parentNodeId
        })
        .request();

      const { data } = response;
      const { body, version, returnCode } = data;

      if (returnCode >= 400) {
        throw new APIError(getReturnCodeMessage(returnCode));
      }

      if (body) {
        if (
          body.financialTemplate &&
          body.financialTemplate.cashFlows === undefined
        ) {
          body.financialTemplate.cashFlows = [];
        }

        if (
          body.financialTemplate &&
          body.financialTemplate.cashFlowsV2 === undefined
        ) {
          body.financialTemplate.cashFlowsV2 = [];
        }

        return {
          spread: body as Spread,
          version
        };
      } else {
        throw new APIError(
          "Could not map unmapped account with id " + unmappedAccountId
        );
      }
    },
    unmapAccount: async (
      spreadId: string,
      nodeId: string,
      isCreditLens: boolean
    ) => {
      const apiUrl = `${getBaseUrl(isCreditLens)}/unmapAccount`;

      const response = await new RequestWrapper(apiUrl)
        .withAuth()
        .setAuthType()
        .asPost({})
        .asJSON()
        .withParams({
          spreadId,
          nodeId
        })
        .request();

      const { data } = response;
      const { body, version, returnCode } = data;

      if (returnCode >= 400) {
        throw new APIError(getReturnCodeMessage(returnCode));
      }

      if (body) {
        if (
          body.financialTemplate &&
          body.financialTemplate.cashFlows === undefined
        ) {
          body.financialTemplate.cashFlows = [];
        }

        if (
          body.financialTemplate &&
          body.financialTemplate.cashFlowsV2 === undefined
        ) {
          body.financialTemplate.cashFlowsV2 = [];
        }

        return {
          spread: body as Spread,
          version
        };
      } else {
        throw new APIError("Could not unmap account with id " + nodeId);
      }
    },
    deleteRules: async (rules: RuleChangeLogItem[], spreadId?: string) => {
      const apiUrl = `${getBaseApiUrl()}/deleteRules`;
      const response = await new RequestWrapper(apiUrl)
        .withAuth()
        .setAuthType()
        .asPost(rules)
        .withParams({ spreadId })
        .asJSON()
        .request();

      const { data } = response;
      const { returnCode } = data;

      if (returnCode >= 400) {
        throw new APIError(getReturnCodeMessage(returnCode));
      }
    },
    undoRule: async (
      spreadId: string,
      currentRule: RuleChangeLogItem,
      previousRule: RuleChangeLogItem,
      isCreditLens: boolean
    ) => {
      const apiUrl = `${getBaseUrl(isCreditLens)}/undoRule`;

      const response = await new RequestWrapper(apiUrl)
        .withAuth()
        .setAuthType()
        .asPost({ spreadId, currentRule, previousRule })
        .asJSON()
        .request();

      const { data } = response;
      const { body, version, returnCode } = data;

      if (returnCode >= 400) {
        throw new APIError(getReturnCodeMessage(returnCode));
      }

      if (body) {
        if (
          body.financialTemplate &&
          body.financialTemplate.cashFlows === undefined
        ) {
          body.financialTemplate.cashFlows = [];
        }

        if (
          body.financialTemplate &&
          body.financialTemplate.cashFlowsV2 === undefined
        ) {
          body.financialTemplate.cashFlowsV2 = [];
        }
        return {
          spread: body as Spread,
          version
        };
      } else {
        throw new APIError(
          "Could not undo rule for  node " + currentRule.nodeId
        );
      }
    },
    getRules: async (entityId: string) => {
      const apiUrl = `${getBaseApiUrl()}/getRulesForEntity`;
      try {
        const response = await new RequestWrapper(apiUrl)
          .withAuth()
          .setAuthType()
          .withParams({ entityId })
          .request();

        const parsedResults = response.data.body as RuleChangeLogItem[];
        return parsedResults;
      } catch (error) {
        throw new APIError("Couldn't get rules for entity");
      }
    }
  },
  comments: {
    getComments: async (spreadId: string, isCreditLens: boolean) => {
      const apiUrl = `${getBaseUrl(isCreditLens)}/getComments`;

      const response = await new RequestWrapper(apiUrl)
        .withAuth()
        .setAuthType()
        .asJSON()
        .withParams({ spreadId })
        .request();

      const { data } = response;
      const { body, returnCode } = data;

      if (returnCode >= 400) {
        throw new APIError(getReturnCodeMessage(returnCode));
      }

      if (body) {
        return body as Comment[];
      } else {
        throw new APIError("Could not get comments");
      }
    },
    addComment: async (
      commentText: string,
      spreadId: string,
      isCreditLens: boolean,
      accountId?: string,
      accountName?: string,
      period?: string
    ) => {
      const apiUrl = `${getBaseUrl(isCreditLens)}/addComment`;
      const response = await new RequestWrapper(apiUrl)
        .withAuth()
        .setAuthType()
        .asPost({})
        .asJSON()
        .withParams({
          commentText: isCreditLens
            ? encodeURIComponent(commentText)
            : commentText,
          spreadId,
          accountId,
          accountName:
            isCreditLens && accountName
              ? encodeURIComponent(accountName)
              : accountName,
          period
        })
        .request();
      // tslint:disable-next-line: no-console
      console.log("addComment response", response);

      const { data } = response;
      const { body, version, returnCode } = data;

      if (returnCode >= 400) {
        throw new APIError(getReturnCodeMessage(returnCode));
      }

      if (body) {
        return {
          comment: body as Comment,
          version
        };
      } else {
        throw new APIError("Could not add new comment");
      }
    },
    addUserToReadByForComments: async (
      comments: Comment[],
      isCreditLens: boolean,
      spreadId: string
    ) => {
      const apiUrl = `${getBaseUrl(isCreditLens)}/addUserToReadBy`;
      const response = isCreditLens
        ? await new RequestWrapper(apiUrl)
            .withCreditLensSpreadIdInHeader(spreadId)
            .asPost(comments)
            .asJSON()
            .request()
        : await new RequestWrapper(apiUrl)
            .withAuth()
            .setAuthType()
            .asPost(comments)
            .asJSON()
            .request();

      const { data } = response;
      const { body, returnCode } = data;

      if (returnCode >= 400) {
        throw new APIError(getReturnCodeMessage(returnCode));
      }

      if (body) {
        return true;
      } else {
        throw new APIError("Could not update read status");
      }
    },
    addUserToReadByForBriefspread: async (spreadId: string) => {
      const apiUrl = `${getBaseApiUrl()}/addUserToReadByBriefspread`;

      const response = await new RequestWrapper(apiUrl)
        .withAuth()
        .setAuthType()
        .asPost({})
        .asJSON()
        .withParams({ spreadId })
        .request();

      const { data } = response;
      const { body, returnCode, version } = data;

      if (returnCode >= 400) {
        throw new APIError(getReturnCodeMessage(returnCode));
      }

      if (body) {
        return {
          version
        };
      } else {
        throw new APIError("Could not update read status");
      }
    }
  },
  image: {
    get: async (
      imageUrl: string,
      isCreditLens: boolean,
      spreadId: string,
      isDbsJwtLoginEnabled: boolean
    ) => {
      const apiUrl = `${getBaseUrl(
        isCreditLens,
        isDbsJwtLoginEnabled
      )}${imageUrl}`;

      if (isCreditLens) {
        try {
          const response = await RequestWrapper.to(apiUrl)
            .withAuth()
            .setAuthType()
            .withOptions({ responseType: "blob" })
            .withParams({ spreadId })
            .request();
          // tslint:disable-next-line: no-console
          console.log("image response", response);

          const image = URL.createObjectURL(response.data);
          return image;
        } catch (error) {
          throw new APIError(`Couldn't get image ${imageUrl}`);
        }
      } else {
        try {
          const response = await RequestWrapper.to(apiUrl)
            .withAuth()
            .setAuthType()
            .withOptions({ responseType: "blob" })
            .request();

          const image = URL.createObjectURL(response.data);
          return image;
        } catch (error) {
          throw new APIError(`Couldn't get image ${imageUrl}`);
        }
      }
    }
  },
  metadata: {
    updateConfigForSpread: async (
      metadata: UpdateMetadataConfigPayload,
      isCreditLens: boolean
    ) => {
      const apiUrl = `${getBaseUrl(isCreditLens)}/updateSpreadMetadata`;
      const { spreadId, spreadVersion, items, isFE } = metadata;
      const params = { spreadId, spreadVersion };
      _.map(items, item => {
        params[item.name] = item.value;
      });

      const response = await new RequestWrapper(apiUrl)
        .withAuth()
        .setAuthType()
        .asPost({ items, isFE })
        .withParams(params)
        .asJSON()
        .request();
      const { data } = response;
      const { body, version, returnCode } = data;

      if (returnCode >= 400) {
        throw new APIError(getReturnCodeMessage(returnCode));
      }

      if (body) {
        return {
          version
        };
      } else {
        throw new APIError("Couldn't update spread metadata");
      }
    },
    getWorkspaceConfig: async (isCreditLens: boolean, spreadId: string) => {
      const apiUrl = `${getBaseUrl(isCreditLens)}/getMetadataConfig`;
      try {
        const response = await new RequestWrapper(apiUrl)
          .withAuth()
          .setAuthType()
          .withParams({ spreadId })
          .request();

        const { data } = response;
        const { body, returnCode } = data;

        if (returnCode < 400 && body) {
          return body as RawMetadataConfig;
        } else {
          throw new APIError(`Couldn't get metadata config`);
        }
      } catch (error) {
        throw new APIError(`Couldn't get metadata config`);
      }
    },
    getClientConfig: async (spreadId: string, isCreditLens?: boolean) => {
      const apiUrl = `${getBaseUrl(
        isCreditLens || false
      )}/getClientMetadataConfig`;
      try {
        const response = await new RequestWrapper(apiUrl)
          .withAuth()
          .setAuthType()
          .withParams({ spreadId })
          .request();

        const { data } = response;
        const { body, returnCode } = data;

        if (returnCode < 400 && body) {
          return body as any;
        } else {
          throw new APIError(`Couldn't get client config`);
        }
      } catch (error) {
        throw new APIError(`Couldn't get client config`);
      }
    }
  },

  tag: {
    save: async (tagAccount, spreadId, isCreditLens: boolean) => {
      const apiUrl = `${getBaseUrl(isCreditLens)}/tag`;
      const response = await new RequestWrapper(apiUrl)
        .withAuth()
        .setAuthType()
        .withParams({
          spreadId
        })
        .asPost(tagAccount)
        .request();

      const { data } = response;
      const { body, version, returnCode } = data;

      if (returnCode >= 400) {
        throw new APIError(getReturnCodeMessage(returnCode));
      }

      if (body) {
        if (
          body.financialTemplate &&
          body.financialTemplate.cashFlows === undefined
        ) {
          body.financialTemplate.cashFlows = [];
        }

        if (
          body.financialTemplate &&
          body.financialTemplate.cashFlowsV2 === undefined
        ) {
          body.financialTemplate.cashFlowsV2 = [];
        }
        return {
          spread: body as Spread,
          version
        };
      } else {
        throw new APIError("Could not create tag account");
      }
    },
    saveArea: async (area, isCreditLens: boolean, spreadId) => {
      const apiUrl = `${getBaseUrl(isCreditLens)}/tag/extractArea`;

      try {
        const response = await new RequestWrapper(apiUrl)
          .withAuth()
          .setAuthType()
          .withParams({ spreadId })
          .asPost(area)
          .request();

        const { data } = response;
        return data.body;
      } catch (error) {
        throw new APIError("Could not extract tag area value");
      }
    }
  },

  preferences: {
    getSupportedLanguages: async () => {
      try {
        const apiUrl = `${getBaseApiUrl()}/getSupportedLanguages`;

        const response = await new RequestWrapper(apiUrl)
          .withAuth()
          .setAuthType()
          .request();

        const { data } = response;
        const { body, returnCode } = data;

        if (returnCode < 400 && body) {
          return body as any;
        } else {
          throw new APIError(`Couldn't get supported languages`);
        }
      } catch (error) {
        throw new APIError(`Couldn't get supported languages`);
      }
    },
    getUserPreferences: async () => {
      try {
        const apiUrl = `${getBaseApiUrl()}/getUserPreferences`;

        const response = await new RequestWrapper(apiUrl)
          .withAuth()
          .setAuthType()
          .request();

        const { data } = response;
        const { body, returnCode } = data;

        if (returnCode < 400 && body) {
          return body as any;
        } else {
          throw new APIError(`Couldn't get user preferences`);
        }
      } catch (error) {
        throw new APIError(`Couldn't get user preferences`);
      }
    },
    saveUserPreferences: async (preferences: Preferences) => {
      const apiUrl = `${getBaseApiUrl()}/saveUserPreferences`;
      const response = await new RequestWrapper(apiUrl)
        .withAuth()
        .setAuthType()
        .asPost(preferences)
        .request();

      const { data } = response;
      const { body, returnCode } = data;

      if (returnCode >= 400) {
        throw new APIError(getReturnCodeMessage(returnCode));
      }

      if (body) {
        return preferences;
      } else {
        throw new APIError("Could update user perferences");
      }
    }
  }
};

export default api;



// WEBPACK FOOTER //
// ./src/api/api.ts